import * as React from "react";

import { cn } from "../lib/utils";

// The auto-growing behavior comes from CSS `field-sizing: content`
// (`field-sizing-content` below). Firefox doesn't support it, so there we
// replicate it by measuring scrollHeight. Any `max-height` on the textarea
// still clamps the fallback, matching the native behavior.
const supportsFieldSizing =
  typeof CSS !== "undefined" && CSS.supports("field-sizing", "content");

function resizeToContent(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  // scrollHeight excludes borders, but border-box height includes them.
  const borders = el.offsetHeight - el.clientHeight;
  el.style.height = `${el.scrollHeight + borders}px`;
}

function Textarea({
  className,
  ref,
  onInput,
  ...props
}: React.ComponentProps<"textarea">) {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Re-measure after every render so controlled value changes (not just user
  // input) resize too. No-op on browsers with native field-sizing support.
  React.useLayoutEffect(() => {
    if (supportsFieldSizing || !innerRef.current) return;
    resizeToContent(innerRef.current);
  });

  return (
    <textarea
      data-slot="textarea"
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      onInput={
        supportsFieldSizing
          ? onInput
          : (event) => {
              resizeToContent(event.currentTarget);
              onInput?.(event);
            }
      }
      className={cn(
        "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base inset-shadow-input-rest transition-[color,box-shadow,border-color] duration-150 ease-out outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className,
      )}
      {...props}
    />
  );
}

export { Textarea };
